import { EmailAccount } from 'pages/dashboard/pages/dispatcher/pages/dispatcher-emails/controllers/types'
import { handleGmailEmailAdd } from './gmail'
import { handleMicrosoftEmailAdd } from './microsoft'
import { handleNylasEmailAdd } from './nylas'

type ShowToastFunction = (message: string, type: 'success' | 'danger') => void

interface EmailProviderPickerCallbacks {
    onSuccess?: (accounts: EmailAccount[]) => void
    onError?: (error: string) => void
    showToast?: ShowToastFunction
    /** Optional copy override (e.g. the "Enable AI email assistant" read-scope upgrade). */
    title?: string
    subtitle?: string
    /** User-facing failure copy rendered inside reconnect flows so it is not hidden behind the modal. */
    errorMessage?: string
    /** Pin the OAuth flow to a known mailbox (read-scope reconnect) so the user skips the account chooser. */
    loginHint?: string
}

interface EmailProviderPickerInstance {
    show: (callbacks: EmailProviderPickerCallbacks) => void
}

let pickerInstance: EmailProviderPickerInstance | null = null

export const setEmailProviderPickerInstance = (instance: EmailProviderPickerInstance | null) => {
    pickerInstance = instance
}

/**
 * Unified email add handler.
 *
 * Shows the provider picker modal (Gmail / Microsoft / Others) whenever a picker instance is mounted.
 * Falls back to Nylas OAuth directly (Nylas has its own built-in provider picker) when none is.
 */
export const handleEmailAdd = async (
    onSuccess?: (accounts: EmailAccount[]) => void,
    onError?: (error: string) => void,
    showToast?: ShowToastFunction,
    opts?: { title?: string; subtitle?: string; errorMessage?: string; loginHint?: string }
) => {
    // Set default connect source for analytics if not already set by the caller (e.g. onboarding)
    if (!sessionStorage.getItem('email_connect_source')) {
        sessionStorage.setItem('email_connect_source', 'settings_page')
    }

    // The provider picker (Gmail / Microsoft / Other) is the single entry point for adding email.
    // "Other (Yahoo, etc.)" inside the picker is what routes through Nylas — there is no separate
    // Nylas fallback here. The picker self-registers when EmailProviderPickerModal mounts at the
    // app root, so this guard should never fail in practice; if it does, surface an error rather
    // than silently connecting via the legacy Nylas flow.
    if (!pickerInstance) {
        console.error('[email-connect] Email provider picker is not mounted')
        onError?.('Unable to open the email connection dialog. Please refresh and try again.')
        return
    }

    pickerInstance.show({
        onSuccess,
        onError,
        showToast,
        title: opts?.title,
        subtitle: opts?.subtitle,
        errorMessage: opts?.errorMessage,
        loginHint: opts?.loginHint,
    })
}

export { handleGmailEmailAdd, handleMicrosoftEmailAdd, handleNylasEmailAdd }
